import { Link } from "@remix-run/react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { useBillingEntitlement } from "~/hooks/queries/use-billing-entitlement";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: Say "billing cycle", never "this month". billing_policy.cycleType is
// `subscription_anniversary`, so a cycle runs from the signup day-of-month, not the 1st.
// A practitioner on a 16th-to-16th cycle read "this month", counted only his calendar-August
// sessions, and reported his allowance as broken (EVO-2133).
// AIDEV-NOTE: Unified color system from billing gallery:
// - warning_80, warning_100: amber-200 border / amber-50 bg / amber-900 text
// - blocked: red-200 border / red-50 bg / red-900 text

interface BillingGlobalBannerDisplayProps {
    thresholdState: "warning_80" | "warning_100" | "blocked";
    usedUnits: number;
    limitUnits: number;
    metricType: "session" | "client";
    /** ISO timestamp when the current billing cycle rolls over. */
    cycleEndAt?: string | null;
    upgradeLinkTo?: string;
}

const bannerStyles: Record<
    BillingGlobalBannerDisplayProps["thresholdState"],
    string
> = {
    warning_80: "border-amber-200 bg-amber-50 text-amber-900",
    warning_100: "border-amber-200 bg-amber-50 text-amber-900",
    blocked: "border-red-200 bg-red-50 text-red-900",
};

// AIDEV-NOTE: Anniversary cycles rarely align with the 1st, so the reset date is the piece
// that lets a user reconcile the count against their own calendar.
function formatCycleReset(
    cycleEndAt: string | null | undefined,
): string | null {
    if (!cycleEndAt) return null;
    const date = new Date(cycleEndAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}

function getBannerCopy(
    thresholdState: BillingGlobalBannerDisplayProps["thresholdState"],
    usedUnits: number,
    limitUnits: number,
    units: string,
    resetLabel: string | null,
): { primary: string; secondary: string | null } {
    const resetSuffix = resetLabel ? ` Resets ${resetLabel}.` : "";
    switch (thresholdState) {
        case "warning_80":
            return {
                primary: `You\u2019re at ${Math.round(
                    (usedUnits / limitUnits) * 100,
                )}% of your ${units} limit this billing cycle (${usedUnits} of ${limitUnits} used).${resetSuffix}`,
                secondary: null,
            };
        case "warning_100":
            return {
                primary: `You\u2019ve used all ${limitUnits} ${units} in this billing cycle.${resetSuffix}`,
                secondary:
                    "New sessions won\u2019t be processed until you upgrade.",
            };
        case "blocked":
            return {
                primary: `Session processing is paused \u2014 you\u2019ve used all ${limitUnits} ${units} included in your plan this period.${resetSuffix}`,
                secondary:
                    "New sessions are still being saved. Upgrade your plan to resume processing.",
            };
    }
}

/**
 * Pure presentation component for the global billing banner.
 * No hooks — accepts all data via props.
 */
export function BillingGlobalBannerDisplay({
    thresholdState,
    usedUnits,
    limitUnits,
    metricType,
    cycleEndAt,
    upgradeLinkTo,
}: BillingGlobalBannerDisplayProps) {
    const units = metricType === "client" ? "clients" : "sessions";
    const copy = getBannerCopy(
        thresholdState,
        usedUnits,
        limitUnits,
        units,
        formatCycleReset(cycleEndAt),
    );
    const linkTo = upgradeLinkTo ?? "/account/membership";

    return (
        <Alert
            className={cn(
                "flex items-center rounded-none border-x-0 border-t-0",
                bannerStyles[thresholdState],
            )}
        >
            <AlertDescription className="flex w-full items-center justify-between gap-4">
                {copy.secondary ? (
                    <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">
                            {copy.primary}
                        </span>
                        <span className="text-sm opacity-80">
                            {copy.secondary}
                        </span>
                    </span>
                ) : (
                    <span className="text-sm font-medium">{copy.primary}</span>
                )}
                <Button asChild size="sm" className="shrink-0">
                    <Link to={linkTo}>Upgrade your plan</Link>
                </Button>
            </AlertDescription>
        </Alert>
    );
}

/**
 * Hooked wrapper that reads feature flags and billing entitlement,
 * then renders BillingGlobalBannerDisplay when appropriate.
 */
export function BillingGlobalBanner() {
    const { data } = useBillingEntitlement();

    if (!data?.enforced || !data.entitlement) {
        return null;
    }

    const { usedUnits, limitUnits, metricType, thresholdState, cycleEndAt } =
        data.entitlement;

    // AIDEV-NOTE: BillingThresholdState includes "active" which doesn't need a banner
    if (
        thresholdState !== "warning_80" &&
        thresholdState !== "warning_100" &&
        thresholdState !== "blocked"
    ) {
        return null;
    }

    return (
        <BillingGlobalBannerDisplay
            thresholdState={thresholdState}
            usedUnits={usedUnits ?? 0}
            limitUnits={limitUnits ?? 0}
            metricType={metricType ?? "session"}
            cycleEndAt={cycleEndAt}
        />
    );
}
